import {Dispatch, FC, SetStateAction, useEffect, useMemo, useState} from "react";
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { Input } from "@jane/lib/src/components/form/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faAdd, faPencil, faPeopleGroup, faPlus} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import {TeamSpace, TeamSpaceTemplate} from "../../../../lib/src/api/repositories/documentation-library-repository";
import {faSpinner} from "@fortawesome/pro-solid-svg-icons";
import { useModal } from "@jane/lib/src/components/layout/ModalProvider";
import { AddTeamSpaceModal } from "../../modals/documentation/AddTeamSpaceModal";
import { useTeamData } from "../../resources/TeamContext";
import { DocumentPageHeader, DocUser } from "../../../../lib/src/components/tiptap/components/DocumentPageHeader";
import { IconButton } from "../../../../lib/src/components/form/Button";
import { usePermissions } from "../../resources/PermissionsHook";
import { useEmployeesData } from "../../resources/EmployeeContext";
import {DocumentationLibraryContextProvider, useDocumentationLibraryContext,} from "../../../../lib/src/components/tiptap/context/DocumentationLibraryContext";
import { TipTapEditor } from "../../../../lib/src/components/tiptap/TipTapEditor";
import {Button} from "@jane/lib/src/components/form/Button";
import {AddTeamSpaceTemplateModal} from "../../modals/AddTeamSpaceTemplateModal";
import {EditTeamSpaceTemplateModal} from "../../modals/EditTeamSpaceTemplateModal";
import {TeamSpaceRow} from "./components/TeamSpaceRow";
import {useParams} from "react-router-dom";

export const DocumentationLibraryPage: FC = () => {
  return <ContentContainer size={"full"}>
    <DocumentationLibraryContextProvider config={config}>
      <DocumentationLibraryPageContent />
    </DocumentationLibraryContextProvider>
  </ContentContainer>
};

export const DocumentationLibraryPageContent: FC = () => {
  const { spaceId, pageId } = useParams()
  const { setSelectedSpace, selectPage, getPages } = useDocumentationLibraryContext();

  // is a space or page is given in the url then load these.
  useEffect(() => {
    if (spaceId) {
      setSelectedSpace(spaceId)
      getPages(spaceId);
    }
    if (pageId) {
      selectPage(pageId)
    }
  }, [spaceId, pageId])

  const { page } = useDocumentationLibraryContext();
  const { employees } = useEmployeesData();
  const [search, setSearch] = useState<string>("");

  const emp = useMemo(() => {
    return employees
      .filter((e) => e.role === "Manager" || e.role === "Coordinator")
      .map((e) => ({ id: e.sub, name: e.name }) as DocUser);
  }, [employees]);

  return (
    <div className={"-mx-6 -my-8"}>
      <div className={"flex items-stretch h-[calc(100vh-5rem)] overflow-hidden"}>
        {" "}
        {/* Calculates height of screen, minus the height of the top brand bar */}
        <div className={"w-64 xl:w-80 h-full overflow-y-auto border-r border-slate-200 bg-white"}>
          <DocumentationNavigator search={search} setSearch={setSearch} />
        </div>
        <div className={"flex-1 h-full flex flex-col overflow-hidden"}>
          <div className={"flex-1 overflow-y-auto"}>
            {page === null ? <div className={"flex flex-col items-center justify-center text-sm my-12 px-8 max-w-md mx-auto text-brand-secondary"}>
                <FontAwesomeIcon icon={faPeopleGroup} className={" text-xl"} />
                <div className={"font-medium mb-2 mt-4"}>Geen werkruimte geselecteerd</div>
                <div>
                  Selecteer een werkruimte in de navigatiebalk links, en maak een pagina aan. Er wordt voor jouw team
                  automatisch een werkruimte aangemaakt.
                </div>
              </div> :
              <DocumentEditor users={emp} />
            }
          </div>
        </div>
      </div>
    </div>
  );
};

const DocumentationNavigator: FC<{ search: string; setSearch: Dispatch<SetStateAction<string>> }> = (props) => {
  const { spaces, templates, onTemplatesUpdated, onSpacesUpdated, isLoadingSpaces } = useDocumentationLibraryContext();
  const { canManageTeamSpaces } = usePermissions();
  const { teams } = useTeamData();
  const permissions = usePermissions()
  const addModal = useModal({
    title: "Werkruimte aanmaken",
    body: <AddTeamSpaceModal teams={teams} onFinished={onSpacesUpdated} />,
    size: "md",
  });

  const filteredSpaces = useMemo<TeamSpace[]>(() => {
    const q = props.search.toLowerCase().trim();

    if (!q) return spaces;

    return spaces
      .map((space): TeamSpace | null => {
        const spaceMatch = space.name.toLowerCase().includes(q);

        if (spaceMatch) {
          return space;
        }

        return null;
      })
      .filter((space): space is TeamSpace => space !== null);
  }, [spaces, props.search]);

  const addTeamSpaceTemplateModal = useModal({title: 'Sjabloon toevoegen', body: <AddTeamSpaceTemplateModal onFinish={() => onTemplatesUpdated()} />})

  return (
    <div className={"px-4 py-3"}>
      <div className={"flex space-x-2 items-end"}>
        {canManageTeamSpaces && (
          <Input type={"search"} label={""} value={props.search} onChange={props.setSearch} placeholder={"Zoeken..."} />
        )}
        {canManageTeamSpaces && <IconButton icon={faPlus} onClick={() => addModal.open()} type={"white"} size={"sm"} />}
      </div>

      <div className={"mt-4"}>
        <div className={"flex flex-row justify-between items-center text-sm font-medium"}>
          <div>Werkruimtes</div>
          <div>({spaces.length})</div>
        </div>
      </div>

      <div className={"mt-1 -mx-2"}>
        {isLoadingSpaces ?
          <div className={"flex flex-col space-y-2 items-center justify-center mt-8"}>
            <FontAwesomeIcon icon={faSpinner} className={"animate-spin text-xl"} />
            <div className={"text-sm"}>Spaces aan het ophalen...</div>
          </div>
         : filteredSpaces.length > 0 ? filteredSpaces.map((ts, index) => {
            return <TeamSpaceRow key={index} space={ts} />;
          }) : <div className={"flex flex-col space-y-1 px-2 mt-4 text-sm text-slate-600"}>
            <div>Er zijn nog geen werkruimtes.</div>
            <div>
              Maak een{" "}
              <span className={"text-brand-primary underline hover:text-brand-500 hover:cursor-pointer"} onClick={() => addModal.open()}>
                nieuwe werkruimte
              </span>{" "}
              aan.
            </div>
          </div>
        }
      </div>

      {permissions.canManageTeamSpaceTemplates && <div className={'flex flex-col items-stretch space-y-2 mt-8'}>
        <div className={"mt-4"}>
          <div className={"flex flex-row justify-between items-center text-sm font-medium"}>
            <div>
              Sjablonen</div>
            <div>({templates.length})</div>
          </div>
        </div>
        <div>
          {templates.map((t, i) => {
            return <TeamSpaceTemplateRow template={t} key={i} />
          })}
        </div>
        <Button type={'secondary'} size={'sm'} text={'Sjabloon toevoegen'} icon={faAdd} onClick={() => addTeamSpaceTemplateModal.open()} />
      </div>}
    </div>
  );
};

const TeamSpaceTemplateRow: FC<{template: TeamSpaceTemplate}> = props => {
  const { onTemplatesUpdated } = useDocumentationLibraryContext();
  const editTeamSpaceTemplateModal = useModal({title: 'Sjabloon bewerken', body: <EditTeamSpaceTemplateModal template={props.template} onFinish={() => onTemplatesUpdated()} />})
  return <div className={'hover:bg-brand-50 hover:cursor-pointer rounded-sm px-2 py-2 -mx-2 flex items-center justify-between'} onClick={() => editTeamSpaceTemplateModal.open()}>
    <div className={'font-medium text-brand-secondary'}>{props.template.name}</div>
    <FontAwesomeIcon icon={faPencil} className={'text-sm'} />
  </div>
}

const DocumentEditor: FC<{ users: DocUser[] }> = (props) => {
  const {
    isLoadingPages,
    editable,
    selectedSpace,
    spaces,
    page,
    content,
    setContent,
    onCancelPageUpdate,
    onPageUpdated,
    onPageDeleted,
    onPageReverted,
    onSpacesUpdated
  } = useDocumentationLibraryContext();
  const { canDeleteTeamSpacePage } = usePermissions();

  const latestUpdate = page
    ? page.versions.reduce((a, b) => (new Date(a.updatedAt) > new Date(b.updatedAt) ? a : b)).updatedAt
    : new Date();

  const space = spaces.find((s) => s.id === selectedSpace)

  return (space != undefined && page != null) ? (
    isLoadingPages ? (
      <div className={"flex items-center justify-center mt-[calc(50vh-5rem)]"}>
        <FontAwesomeIcon icon={faSpinner} className={"animate-spin text-4xl"} />
      </div>
    ) : (
      <div className={""}>
        <DocumentPageHeader
          config={config}
          space={space}
          page={page}
          lastUpdated={latestUpdate}
          versions={page.versions}
          canDeletePage={canDeleteTeamSpacePage}
          users={props.users ?? []}
          onCancel={onCancelPageUpdate}
          onFinished={onPageUpdated}
          onSpaceUpdated={onSpacesUpdated}
          onDeleted={onPageDeleted}
          onPageReverted={onPageReverted}
        />
        <div>
          {content &&
            (editable ? (
              <TipTapEditor
                showMenuBar={true}
                title={page.name}
                lastEdited={latestUpdate}
                editable={true}
                menuBarType={"top"}
                content={content}
                setContent={setContent}
              />
            ) : (
              <TipTapEditor
                showMenuBar={true}
                editable={false}
                title={page.name}
                lastEdited={latestUpdate}
                menuBarType={"top"}
                content={content}
                setContent={setContent}
              />
            ))}
        </div>
      </div>
    )
  ) : (
    <div className={"flex flex-col items-center justify-center my-12"}>
      <div>Geen werkruimte geselecteerd</div>
      <div>Selecteer een werkruimte in het menu links hiervan</div>
      <div>Mocht er nog geen werkruimte bestaan, maak deze dan aan.</div>
    </div>
  );
};